








































































































import { TaskItem } from '@/shared/task-item';
import { TaskType } from '@/shared/task-type';
import { ClientOriginalSourceType } from '@/shared/types/client-original-source-type';
import { User } from '@/shared/user';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const partnerPracticesModule = namespace('partnerPractices');
const typesModule = namespace('types');
const userModule = namespace('user');
const usersModule = namespace('users');

@Component
export default class TasksTable extends Vue {
    @partnerPracticesModule.Getter('partnerPracticeName') private partnerPracticeName!: (partnerPracticeId: string | null) => string;

    @typesModule.Getter('activeClientOriginalSourceTypes') private clientOriginalSourceTypes!: Array<ClientOriginalSourceType>;

    @userModule.Getter('user') private currentUser!: User;
    @userModule.Getter('isAdministrator') public isAdministrator!: boolean;
    @userModule.Getter('isInternalKeyStakeholder') public isInternalKeyStakeholder!: boolean;
    @userModule.Getter('isCustomerExperienceExecutiveTeamLeader') public isCustomerExperienceExecutiveTeamLeader!: boolean;
    @userModule.Getter('isHeadOfCustomerExperienceTeam') public isHeadOfCustomerExperienceTeam!: boolean;
    @userModule.Getter('isDataCreationTeamLeader') public isDataCreationTeamLeader!: boolean;
    @userModule.Getter('isDataCreationExecutive') public isDataCreationExecutive!: boolean;
    @userModule.Getter('isHeadOfDepartment') public isHeadOfDepartment!: boolean;
    @userModule.Getter('isLegalAssistant') public isLegalAssistant!: boolean;
    @userModule.Getter('isLegalAdvisor') public isLegalAdvisor!: boolean;

    @usersModule.Getter('users') private users!: Array<User>;

    @Prop({ required: true }) public tasks!: Array<TaskItem>;
    @Prop({ type: Number }) public currentPage!: number;
    @Prop({ type: Number }) public pageSize!: number;
    @Prop({ type: Number }) public total!: number;

    private currentPageNumber: number = this.currentPage;
    private loading: boolean = false;
    private options: Array<TaskItem & { selected: boolean }> = new Array<TaskItem & { selected: boolean }>();
    private selectedTasks: Array<TaskItem & { selected: boolean }> = new Array<TaskItem & { selected: boolean }>();
    private selectAll: boolean = false;
    private unassignTasksConfirmationDialogVisible: boolean = false;

    public get paginationDescription(): string {
        const start = (this.tasks.length < 1)
            ? 0
            : ((this.currentPage - 1) * this.pageSize) + 1;

        const end = start + this.pageSize > this.total
            ? this.total
            : start + this.pageSize - 1;

        return `Showing ${start.toLocaleString()} - ${end.toLocaleString()} of ${this.total.toLocaleString()}`;
    }

    private get canAssignTasks(): boolean {
        return this.selectedTasks.length > 0 &&
            (
                this.isCustomerExperienceExecutiveTeamLeader ||
                this.isHeadOfCustomerExperienceTeam ||
                this.isDataCreationTeamLeader ||
                this.isInternalKeyStakeholder ||
                this.isAdministrator
            );
    }

    private get canUnassignTasks(): boolean {
        return this.selectedTasks.length > 0 && this.selectedTasks.some((task) => task.users.length > 0) &&
            (
                this.isCustomerExperienceExecutiveTeamLeader ||
                this.isHeadOfCustomerExperienceTeam ||
                this.isDataCreationTeamLeader ||
                this.isInternalKeyStakeholder ||
                this.isAdministrator
            );
    }

    public get assignTasksText(): string {
        if (this.selectedTasks == null || !this.selectedTasks.length) {
            return 'Assign Task';
        }

        const includesAssigned = this.selectedTasks.some((task) => task.users.length > 0);

        if (this.selectedTasks.length === 1 && includesAssigned) {
            return 'Re-assign Task';
        } else if (this.selectedTasks.length === 1) {
            return 'Assign Task';
        } else if (this.selectedTasks.length > 1 && includesAssigned) {
            return 'Re-assign Tasks';
        }

        return 'Assign Tasks';
    }

    private get unassignTasksText(): string {
        if (this.selectedTasks == null || !this.selectedTasks.length) {
            return 'Unassign Task';
        }

        if (this.selectedTasks.length > 1 && this.selectedTasks.some((task) => task.users.length > 0)) {
            return 'Unassign Tasks';
        }

        return 'Unassign Task';
    }

    private get canDelete(): boolean {
        return this.isAdministrator || this.isCustomerExperienceExecutiveTeamLeader || this.isHeadOfCustomerExperienceTeam || this.isDataCreationTeamLeader;
    }

    @Watch('currentPageNumber')
    private currentPageNumberChanged(value: number): void {
        this.$emit('page-changed', value);
    }

    @Watch('selectAll')
    private onSelectAllChanged(selectAll: boolean) {
        if (selectAll) {
            this.options.filter((option) => option.outcome == null).forEach((option) => {
                option.selected = true;
            });
        } else {
            this.options.filter((option) => option.outcome == null).forEach((option) => {
                option.selected = false;
            });
        }
    }

    @Watch('tasks')
    private onTasksChanged(tasks: Array<TaskItem>): void {
        this.options = new Array<TaskItem & { selected: true }>();
        tasks.forEach((task) => {
            this.options.push({ ...task, selected: false });
        });
    }

    @Watch('options', { deep: true })
    private onOptionsChanged(options: Array<TaskItem & { selected: boolean }>): void {
        this.selectedTasks = options.filter((option) => option.selected);
    }

    @Watch('selectedTasks')
    private onSelectedTasksChanged(selectedTasks: Array<TaskItem & { selected: boolean }>): void {
        if (!selectedTasks.length) {
            this.selectAll = false;
        }
    }

    private accountNumber(task: TaskItem): string {
        if (task == null || task.client == null) {
            return '';
        }

        return `${task.client.accountNo}`;
    }

    private clientName(task: TaskItem): string {
        if (task == null || task.client == null) {
            return '';
        }

        return `${task.client.firstName} ${task.client.lastName}`;
    }

    private originalSourceDescription(task: TaskItem): string {
        if (task == null || task.client == null) {
            return '';
        }

        const clientOriginalSourceType = this.clientOriginalSourceTypes.find((type) => type.id === task.client!.originalSourceId);
        return clientOriginalSourceType == null ? '' : clientOriginalSourceType.value;
    }

    private appointmentDate(task: TaskItem): string | Date | null {
        if (task == null || task.appointment == null) {
            return null;
        }

        return task.appointment.date;
    }

    private taskTypeDescription(task: TaskItem): string | null {
        switch (task.type) {
            case TaskType.RetrieveWillPacket:
                return 'Retrieve Will Packet';
            case TaskType.SendConfirmationLetter:
                return 'Send Confirmation Letter';
            default:
                return '';
        }
    }

    private taskIsOverdue(task: TaskItem): boolean {
        if (task == null) {
            return false;
        }

        return task.outcome == null && task.dueDate != null && new Date(task.dueDate.toString()) < new Date();
    }

    private formatAssignedUsers(task: TaskItem): string {
        const names = task.users.map((user) => `${user.firstName!.trim()} ${user.lastName!.trim()}`.trim());
        return names
            .sort((a, b) => {
                if (a > b) { return 1; }
                if (a < b) { return -1; }
                return 0;
            })
            .join(', ');
    }

    private completedByUserName(task: TaskItem): string {
        if (task.outcome == null || task.outcome.createdBy == null) {
            return '';
        }

        const user = this.users.find((u) => u.id === task.outcome!.createdBy!);

        return user == null ? '' : `${user.firstName == null ? '' : user.firstName} ${user.lastName == null ? '' : user.lastName}`;
    }

    public canViewClient(task: TaskItem): boolean {
        return task.client != null;
    }

    private canComplete(task: TaskItem): boolean {
        return task.outcome == null &&
            (
                this.isAdministrator ||
                this.isInternalKeyStakeholder ||
                task.users.some((user) => user.id === this.currentUser.id)
            );
    }

    private canEdit(task: TaskItem): boolean {
        return task.outcome != null && this.isAdministrator;
    }

    private async showAssignTasksDialog(): Promise<void> {
        this.$emit('show-assign-tasks-dialog', [...this.selectedTasks]);
    }

    private showUnassignTasksConfirmation(): void {
        this.unassignTasksConfirmationDialogVisible = true;
    }

    private onUnassignTasksConfirmed(): void {
        this.$emit('unassign-tasks-confirmed', [...this.selectedTasks]);
        this.unassignTasksConfirmationDialogVisible = false;
    }

    private onUnassignTasksCancelled(): void {
        this.unassignTasksConfirmationDialogVisible = false;
    }

    private showCompleteTaskDialog(task: TaskItem): void {
        this.$emit('show-complete-task-dialog', { ...task });
    }

    private showEditTaskDialog(task: TaskItem): void {
        this.$emit('show-edit-task-dialog', { ...task });
    }

    private showDeleteTaskConfirmation(task: TaskItem): void {
        this.$emit('show-delete-task-confirmation', { ...task });
    }
}

